import type { HTMLAttributes } from "react";
import {
  brandColor,
  CAIRE_WORDMARK,
  CAIRE_DESCRIPTORS,
  type CaireWordmarkVariant,
} from "@appcaire/brand";

interface CaireWordmarkProps extends HTMLAttributes<HTMLSpanElement> {
  /** Applies to the two dots, never to the i stem. */
  iClassName?: string;
  accentClassName?: string;
  variant?: CaireWordmarkVariant;
  showDescriptor?: boolean;
  locale?: "sv" | "en";
}

export function CaireWordmark({
  accentClassName,
  className,
  iClassName,
  variant = "standard",
  showDescriptor = false,
  locale = "sv",
  ...props
}: CaireWordmarkProps) {
  const ink = variant === "inverse" ? brandColor("onAction") : "currentColor";
  const dots =
    variant === "monochrome"
      ? ink
      : brandColor(variant === "inverse" ? "accent" : "action");
  return (
    <span className={className} {...props}>
      <span className="sr-only">Caire</span>
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox={CAIRE_WORDMARK.viewBox}
        style={{
          display: "inline-block",
          height: "1em",
          width: "3.48em",
          verticalAlign: "-0.05em",
          overflow: "visible",
        }}
      >
        <g transform={`translate(0 ${CAIRE_WORDMARK.baseline}) scale(1 -1)`}>
          {CAIRE_WORDMARK.shapes.map(({ d, x, y = 0, part }, index) => (
            <path
              key={index}
              data-part={part}
              data-testid={part === "dot" ? "caire-wordmark-i" : undefined}
              transform={`translate(${x} ${y})`}
              d={d}
              className={
                part === "letters" ? undefined : (iClassName ?? accentClassName)
              }
              style={{
                fill:
                  part === "letters"
                    ? ink
                    : iClassName || accentClassName
                      ? "currentColor"
                      : dots,
              }}
            />
          ))}
        </g>
      </svg>
      {showDescriptor ? (
        <span
          style={{
            display: "block",
            marginTop: "0.5em",
            fontFamily: "var(--cb-font-sans)",
            fontSize: "max(12px, 0.38em)",
            fontWeight: 400,
            lineHeight: 1.4,
          }}
        >
          {CAIRE_DESCRIPTORS[locale]}
        </span>
      ) : null}
    </span>
  );
}
